import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { CodedStatusMessage } from '@core/models/coded-status-message.model';
import { ICONS } from '@core/services/icons/icons.service';
import { ReferenceNamesService } from '@core/services/reference-names/reference-names.service';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import { isNil } from '@core/utils/shared-utils';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { StatusPillComponent } from '@shared/components/status-pill/status-pill.component';

import { isSiteBlocker } from '../deploy-site-warnings.utils';
import { DeployWarningsListComponent } from '../deploy-warnings-list/deploy-warnings-list.component';

/** One exportable entity: its uid, its resolved name if one came back, and whether it is picked. */
interface SiteRow {
  uid: string;
  name: string | null;
  selected: boolean;
  deployed: boolean;
  /** Everything the back reports about this site, blockers included. */
  warnings: CodedStatusMessage[];
  /** Whether ticking this site holds the whole file back. */
  blocked: boolean;
}

/**
 * The entities a view-kind channel can export, with the subset the configuration restricts it
 * to. A view-kind response carries only `valid_uids`, so the names are resolved separately and
 * a row falls back to its uid when none comes back — uids like `E40` do not say which
 * warehouse they are.
 */
@Component({
  selector: 'app-deploy-sites-table',
  imports: [
    TranslatePipe,
    CheckboxComponent,
    StatusPillComponent,
    DeployWarningsListComponent
  ],
  templateUrl: './deploy-sites-table.component.html',
  styles: ``
})
export class DeploySitesTableComponent {
  readonly uids = input.required<string[]>();
  readonly entityType = input.required<string | null>();
  readonly planId = input.required<string | null>();
  readonly selectedUids = input.required<ReadonlySet<string>>();
  /**
   * Shown but not editable. A tick here is not a staged selection: it PATCHes
   * `warehouse_uids` on the configuration straight away, so on a channel that can no longer
   * send it would rewrite live config to no purpose. The rows still render, because what the
   * last export covered is exactly what the operator comes here to read.
   */
  readonly locked = input(false);
  /**
   * The rows the last export covered, or `null` when nothing is known to have gone out. The
   * caller owns the inference — this channel keeps no per-site send record, so the set cannot
   * be derived from anything a row carries.
   */
  readonly deployedUids = input<ReadonlySet<string> | null>(null);
  /**
   * The warnings about each site. One naming an unvalidated route blocks the site: while it is
   * ticked, the whole file is refused, so its row stays editable for the operator to untick.
   */
  readonly siteWarnings = input<ReadonlyMap<string, CodedStatusMessage[]>>(new Map());

  readonly toggleUid = output<string>();
  readonly selectionRequested = output<string[]>();

  private readonly store = inject(Store);
  private readonly referenceNames = inject(ReferenceNamesService);
  private readonly dataspaceState = this.store.selectSignal(selectDataspaceState);

  protected readonly ICONS = ICONS;
  protected readonly columnCount = 4;

  private readonly names = signal<ReadonlyMap<string, string>>(new Map());
  private readonly expandedUids = signal<ReadonlySet<string>>(new Set());

  protected readonly rows = computed<SiteRow[]>(() => {
    const names = this.names();
    const selected = this.selectedUids();
    const deployed = this.deployedUids();
    const siteWarnings = this.siteWarnings();
    return this.uids().map((uid) => {
      const warnings = siteWarnings.get(uid) ?? [];
      return {
        uid,
        name: names.get(uid) ?? null,
        selected: selected.has(uid),
        deployed: deployed?.has(uid) ?? false,
        warnings,
        blocked: warnings.some(isSiteBlocker)
      };
    });
  });

  protected readonly allSelected = computed(() => {
    const rows = this.rows();
    return rows.length > 0 && rows.every((row) => row.selected);
  });

  protected readonly someSelected = computed(
    () => this.rows().some((row) => row.selected) && !this.allSelected()
  );

  constructor() {
    // The data space arrives from the store after the panel is already rendered, so this
    // re-reads it rather than resolving once. `resolveNames$` caches and degrades on
    // failure, which is why there is no error branch: a row keeps its uid.
    effect(() => {
      const entityType = this.entityType();
      const uids = this.uids();
      const dataSpaceId = this.dataspaceState().dataspace?.id;
      if (isNil(entityType) || isNil(dataSpaceId) || uids.length === 0) return;
      const planId = this.planId();

      untracked(() => {
        this.referenceNames
          .resolveNames$(entityType, uids, {
            dataSpaceId,
            ...(isNil(planId) ? {} : { planId })
          })
          .subscribe((names) => this.names.set(names));
      });
    });
  }

  protected isExpanded(uid: string): boolean {
    return this.expandedUids().has(uid);
  }

  protected toggleDetail(uid: string): void {
    this.expandedUids.update((expanded) => {
      const next = new Set(expanded);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }

  protected onToggleAll(): void {
    this.selectionRequested.emit(
      this.allSelected() ? [] : this.rows().map((row) => row.uid)
    );
  }
}
